import React, { useState, useMemo, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Copy, Check } from 'lucide-react';
import type { List, Section } from '@/types';

interface ExportListDialogProps {
  open: boolean;
  onClose: () => void;
  list: List | null;
  sections: Section[];
}

export default function ExportListDialog({ open, onClose, list, sections }: ExportListDialogProps) {
  const [headerFormat, setHeaderFormat] = useState('strong'); // plain, emphasis, strong
  const [itemPrefix, setItemPrefix] = useState('dash'); // plain, space, tab, dash, asterisk
  const [itemFilter, setItemFilter] = useState('all'); // all, selected, unselected
  const [isCopied, setIsCopied] = useState(false);

  const generatedText = useMemo(() => {
    if (!list || !sections) return "";
    
    let output = "";
    const prefixes: Record<string, string> = {
      plain: "",
      space: "  ",
      tab: "\t",
      dash: "- ",
      asterisk: "* ",
    };
    const itemPrefixStr = prefixes[itemPrefix] || "- ";

    sections.forEach((section, index) => {
      // Filter items based on selection
      let filteredItems = section.items || [];
      if (itemFilter === 'selected') {
        filteredItems = (section.items || []).filter(item => {
          if (item.isInherited) {
            const inheritanceKey = `${item.id}_${list.id}`;
            const states = JSON.parse(localStorage.getItem('inheritedItemStates') || '{}');
            return !!states[inheritanceKey];
          }
          return item.completed;
        });
      } else if (itemFilter === 'unselected') {
        filteredItems = (section.items || []).filter(item => {
          if (item.isInherited) {
            const inheritanceKey = `${item.id}_${list.id}`;
            const states = JSON.parse(localStorage.getItem('inheritedItemStates') || '{}');
            return !states[inheritanceKey];
          }
          return !item.completed;
        });
      }

      // Only add section header if there are items to show
      if (filteredItems.length > 0) {
        if (section.title) {
          switch (headerFormat) {
            case 'emphasis':
              output += `*${section.title}*\n`;
              break;
            case 'strong':
              output += `**${section.title}**\n`;
              break;
            default: // plain
              output += `${section.title}\n`;
          }
        } else if (sections.length > 1) { 
          output += `**Untitled Section**\n`;
        }

        filteredItems.forEach(item => {
          output += `${itemPrefixStr}${item.content}\n`;
        });

        // Add spacing between sections only if this isn't the last section with content
        const remainingSections = sections.slice(index + 1);
        const hasMoreSections = remainingSections.some(s => {
          let items = s.items || [];
          if (itemFilter === 'selected') {
            items = (s.items || []).filter(i => i.isInherited 
              ? JSON.parse(localStorage.getItem('inheritedItemStates') || '{}')[`${i.id}_${list.id}`] 
              : i.completed);
          } else if (itemFilter === 'unselected') {
            items = (s.items || []).filter(i => i.isInherited 
              ? !JSON.parse(localStorage.getItem('inheritedItemStates') || '{}')[`${i.id}_${list.id}`] 
              : !i.completed);
          }
          return items.length > 0;
        });
        
        if (hasMoreSections) {
          output += "\n";
        }
      }
    });

    return output;
  }, [list, sections, headerFormat, itemPrefix, itemFilter]);

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedText);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  useEffect(() => {
    if (!open) {
      setIsCopied(false);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl max-h-[80vh] flex flex-col p-0">
        <DialogHeader className="p-4 md:p-6 pb-2 border-b">
          <DialogTitle>Export List: {list?.title}</DialogTitle>
          <DialogDescription>
            Configure the format and copy the generated text.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 p-4 md:p-6">
            {/* Options */}
            <div className="space-y-6 md:col-span-1">
              <div>
                <Label className="font-semibold">Header Style</Label>
                <RadioGroup value={headerFormat} onValueChange={setHeaderFormat} className="mt-2 space-y-2">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="plain" id="h-plain" />
                    <Label htmlFor="h-plain">Plain</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="emphasis" id="h-emphasis" />
                    <Label htmlFor="h-emphasis" className="font-mono italic">*Emphasis*</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="strong" id="h-strong" />
                    <Label htmlFor="h-strong" className="font-mono font-bold">**Strong**</Label>
                  </div>
                </RadioGroup>
              </div>
              <div>
                <Label className="font-semibold">List Item Style</Label>
                <RadioGroup value={itemPrefix} onValueChange={setItemPrefix} className="mt-2 space-y-2">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="plain" id="i-plain" />
                    <Label htmlFor="i-plain">Plain</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="dash" id="i-dash" />
                    <Label htmlFor="i-dash" className="font-mono">- Item</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="asterisk" id="i-asterisk" />
                    <Label htmlFor="i-asterisk" className="font-mono">* Item</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="space" id="i-space" />
                    <Label htmlFor="i-space" className="font-mono">[space] Item</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="tab" id="i-tab" />
                    <Label htmlFor="i-tab" className="font-mono">[tab] Item</Label>
                  </div>
                </RadioGroup>
              </div>
              <div>
                <Label className="font-semibold">Items to Export</Label>
                <RadioGroup value={itemFilter} onValueChange={setItemFilter} className="mt-2 space-y-2">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="all" id="f-all" />
                    <Label htmlFor="f-all">All Items</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="selected" id="f-selected" />
                    <Label htmlFor="f-selected">Selected Only</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="unselected" id="f-unselected" />
                    <Label htmlFor="f-unselected">Unselected Only</Label>
                  </div>
                </RadioGroup>
              </div>
            </div>

            {/* Output */}
            <div className="md:col-span-2 flex flex-col">
              <Label className="font-semibold mb-2">Generated Text</Label>
              <Textarea
                readOnly
                value={generatedText}
                className="flex-1 font-mono text-sm resize-none bg-slate-50 min-h-[150px] sm:min-h-[200px]"
              />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 p-4 md:p-6 border-t">
          <Button
            variant="outline"
            onClick={onClose}
            className="border-slate-200 hover:bg-slate-50"
          >
            Close
          </Button>
          <Button onClick={handleCopy} className="bg-blue-600 hover:bg-blue-700 w-32">
            {isCopied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
            {isCopied ? 'Copied!' : 'Copy'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
